// src/components/Header.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingBag, User, Search, LogOut, Package, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '@/context/CartContext.jsx';
import { useAuth } from '@/context/AuthContext.jsx';

export default function Header() {
  const [isScrolled,       setIsScrolled]       = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu,     setShowUserMenu]     = useState(false);
  const [showSearch,       setShowSearch]       = useState(false);
  const [searchQuery,      setSearchQuery]      = useState('');
  const location  = useLocation();
  const navigate  = useNavigate();
  const userMenuRef = useRef(null);
  const searchRef   = useRef(null);

  const { itemCount } = useCart();
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on navigation
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowUserMenu(false);
    setShowSearch(false);
  }, [location]);

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus search input when shown
  useEffect(() => {
    if (showSearch) searchRef.current?.focus();
  }, [showSearch]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearch(false);
      setSearchQuery('');
    }
  };

  const navLinks = [
    { name: 'Home',    path: '/' },
    { name: 'Shop',    path: '/shop' },
    { name: 'About',   path: '/about' },
    { name: 'Blog',    path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <>
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-[hsl(var(--cream-white))]/95 backdrop-blur-md border-b border-[hsl(var(--soft-grey))] py-3 shadow-sm'
          : 'bg-transparent py-5'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">

            {/* Logo */}
            <Link to="/" className="flex-shrink-0 flex items-center gap-2 group">
              <img
                src="https://hubluonline.co.za/images/04208033ef846f3bbe651a2d41a99d5c.png"
                alt="HUBLU Icon"
                className="w-8 h-8 md:w-10 md:h-10 object-contain transition-transform duration-300 group-hover:scale-105"
              />
              <img
                src="https://hubluonline.co.za/images/name.png"
                alt="HUBLU Text"
                className="w-24 md:w-28 h-auto object-contain transition-transform duration-300 group-hover:scale-105"
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex space-x-1">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="relative px-4 py-2 text-base font-medium text-[hsl(var(--charcoal))] group"
                >
                  <span className={`relative z-10 transition-colors duration-200 ${
                    isActive(link.path) ? 'text-[hsl(var(--primary))]' : 'group-hover:text-[hsl(var(--accent))]'
                  }`}>
                    {link.name}
                  </span>
                  {isActive(link.path) && (
                    <motion.div
                      layoutId="nav-indicator"
                      className="absolute inset-0 bg-[hsl(var(--accent))]/10 rounded-full"
                      transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  {!isActive(link.path) && (
                    <div className="absolute bottom-1 left-4 right-4 h-0.5 bg-[hsl(var(--accent))] scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 ease-out" />
                  )}
                </Link>
              ))}
            </nav>

            {/* Desktop Icons */}
            <div className="hidden md:flex items-center space-x-1">

              {/* Search */}
              <button
                aria-label="Search"
                onClick={() => setShowSearch(s => !s)}
                className={`p-2 rounded-full transition-all duration-200 ${
                  showSearch
                    ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'
                    : 'text-[hsl(var(--charcoal))] hover:text-[hsl(var(--accent))] hover:bg-[hsl(var(--soft-grey))]'
                }`}
              >
                <Search className="w-5 h-5" />
              </button>

              {/* User menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  aria-label="Account"
                  onClick={() => setShowUserMenu(s => !s)}
                  className="flex items-center gap-1 p-2 text-[hsl(var(--charcoal))] hover:text-[hsl(var(--accent))] hover:bg-[hsl(var(--soft-grey))] rounded-full transition-all duration-200"
                >
                  <User className="w-5 h-5" />
                  {user && <ChevronDown className="w-3 h-3" />}
                </button>

                <AnimatePresence>
                  {showUserMenu && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -8 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl shadow-xl border border-[hsl(var(--soft-grey))] overflow-hidden z-50"
                    >
                      {user ? (
                        <>
                          <div className="px-4 py-3 border-b border-[hsl(var(--soft-grey))]">
                            <p className="font-semibold text-sm text-[hsl(var(--charcoal))] truncate">{user.name}</p>
                            <p className="text-xs text-[hsl(var(--charcoal))]/50 truncate">{user.email}</p>
                          </div>
                          <Link to="/account" className="flex items-center gap-2 px-4 py-3 text-sm text-[hsl(var(--charcoal))] hover:bg-[hsl(var(--soft-grey))] transition-colors">
                            <Package className="w-4 h-4 text-[hsl(var(--primary))]" /> My Orders
                          </Link>
                          <Link to="/account?tab=profile" className="flex items-center gap-2 px-4 py-3 text-sm text-[hsl(var(--charcoal))] hover:bg-[hsl(var(--soft-grey))] transition-colors">
                            <User className="w-4 h-4 text-[hsl(var(--primary))]" /> Profile
                          </Link>
                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-500 hover:bg-red-50 transition-colors border-t border-[hsl(var(--soft-grey))]"
                          >
                            <LogOut className="w-4 h-4" /> Sign Out
                          </button>
                        </>
                      ) : (
                        <>
                          <Link to="/login" className="flex items-center gap-2 px-4 py-3 text-sm font-semibold text-[hsl(var(--charcoal))] hover:bg-[hsl(var(--soft-grey))] transition-colors">
                            <User className="w-4 h-4 text-[hsl(var(--primary))]" /> Sign In
                          </Link>
                          <Link to="/register" className="flex items-center gap-2 px-4 py-3 text-sm text-[hsl(var(--charcoal))] hover:bg-[hsl(var(--soft-grey))] transition-colors border-t border-[hsl(var(--soft-grey))]">
                            <Package className="w-4 h-4 text-[hsl(var(--primary))]" /> Create Account
                          </Link>
                        </>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Cart */}
              <Link
                to="/cart"
                aria-label={`Cart (${itemCount} items)`}
                className="relative p-2 text-[hsl(var(--charcoal))] hover:text-[hsl(var(--accent))] hover:bg-[hsl(var(--soft-grey))] rounded-full transition-all duration-200"
              >
                <ShoppingBag className="w-5 h-5" />
                <AnimatePresence>
                  {itemCount > 0 && (
                    <motion.span
                      key={itemCount}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-[hsl(var(--primary))] text-white text-[10px] font-bold flex items-center justify-center rounded-full px-1"
                    >
                      {itemCount > 99 ? '99+' : itemCount}
                    </motion.span>
                  )}
                </AnimatePresence>
              </Link>
            </div>

            {/* Mobile Icons */}
            <div className="md:hidden flex items-center gap-1">
              <Link
                to="/cart"
                aria-label="Cart"
                className="relative p-2 text-[hsl(var(--charcoal))]"
              >
                <ShoppingBag className="w-6 h-6" />
                {itemCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-[hsl(var(--primary))] text-white text-[10px] font-bold flex items-center justify-center rounded-full px-1">
                    {itemCount > 99 ? '99+' : itemCount}
                  </span>
                )}
              </Link>
              <button
                onClick={() => setIsMobileMenuOpen(s => !s)}
                className="p-2 text-[hsl(var(--charcoal))]"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
              </button>
            </div>
          </div>

          {/* Desktop Search Bar (slide down) */}
          <AnimatePresence>
            {showSearch && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <form onSubmit={handleSearch} className="pb-3 pt-2">
                  <div className="relative max-w-md mx-auto">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--charcoal))]/40" />
                    <input
                      ref={searchRef}
                      type="text"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search products…"
                      className="w-full pl-10 pr-12 py-2.5 rounded-full border-2 border-[hsl(var(--soft-grey))] bg-white focus:border-[hsl(var(--accent))] focus:outline-none focus:ring-4 focus:ring-[hsl(var(--accent))]/20 text-[hsl(var(--charcoal))] text-sm transition-all"
                    />
                    {searchQuery && (
                      <button type="button" onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-[hsl(var(--charcoal))]/40 hover:text-[hsl(var(--charcoal))]">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Mobile Nav */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden overflow-hidden bg-[hsl(var(--cream-white))] border-b border-[hsl(var(--soft-grey))]"
            >
              <div className="px-4 pt-2 pb-5 space-y-1 shadow-inner">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                      isActive(link.path)
                        ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'
                        : 'text-[hsl(var(--charcoal))] hover:bg-[hsl(var(--soft-grey))]'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}

                <div className="border-t border-[hsl(var(--soft-grey))] pt-3 mt-2 space-y-1">
                  {user ? (
                    <>
                      <div className="px-4 py-2">
                        <p className="text-xs text-[hsl(var(--charcoal))]/50">Signed in as</p>
                        <p className="font-semibold text-sm text-[hsl(var(--charcoal))] truncate">{user.name}</p>
                      </div>
                      <Link to="/account" className="flex items-center gap-2 px-4 py-3 rounded-xl text-[hsl(var(--charcoal))] hover:bg-[hsl(var(--soft-grey))] text-sm font-medium">
                        <Package className="w-4 h-4 text-[hsl(var(--primary))]" /> My Orders & Account
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 text-sm font-medium transition-colors"
                      >
                        <LogOut className="w-4 h-4" /> Sign Out
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" className="flex items-center gap-2 px-4 py-3 rounded-xl text-[hsl(var(--charcoal))] hover:bg-[hsl(var(--soft-grey))] text-sm font-medium">
                        <User className="w-4 h-4 text-[hsl(var(--primary))]" /> Sign In
                      </Link>
                      <Link to="/register" className="flex items-center gap-2 px-4 py-3 rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] text-sm font-bold">
                        <Package className="w-4 h-4" /> Create Account
                      </Link>
                    </>
                  )}
                </div>

                {/* Mobile search */}
                <form onSubmit={handleSearch} className="pt-2 px-1">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--charcoal))]/40" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search products…"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-transparent bg-[hsl(var(--soft-grey))] focus:border-[hsl(var(--accent))] focus:outline-none text-[hsl(var(--charcoal))] text-sm transition-all"
                    />
                  </div>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
}